// App.js
import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
} from "react-router-dom";
import Welcome from "./welcome";
import CreateSociety from "./CreateSociety";
import SocietyList from "./SocietyList";
import CreateSocietyAdmin from "./CreateSocietyAdmin";
import CreateSocietyAdminList from "./CreateSocietyAdminList";
import AddSocietyAdmin from "./AddSocietyAdmin";
import axios from "axios";
import logo from "./image/logo3.png";
import AllLoginAdmin from "./AllLoginAdmin";
import { BASE_URL } from "./APIs/config";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import PrivateRoute from "./PrivateRoute";


// Import icons (install react-icons: npm install react-icons)
import { FiMail, FiLock, FiLogIn, FiEye, FiEyeOff } from "react-icons/fi";
import { HiOutlineShieldCheck } from "react-icons/hi";
import Comments from "./Comments";
import RequestCall from "./RequestCall";
import UserQuery from "./UserQuery";
import RequestDemo from "./RequestDemo";

function Login() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const validateTokenAndRole = () => {
      const token = localStorage.getItem("token");
      const loginTime = localStorage.getItem("loginTime");
      if (!token || !loginTime) {
        setChecking(false);
        return;
      }

      try {
        const now = Date.now();
        const maxAge = 24 * 60 * 60 * 1000;
        if (now - parseInt(loginTime, 10) >= maxAge) {
          localStorage.removeItem("token");
          localStorage.removeItem("loginTime");
          localStorage.removeItem("role");
          setChecking(false);
          return;
        }

        const decoded = jwtDecode(token);
        const role = decoded?.role ?? decoded?.roles ?? null;

        if (
          role === "SUPER_ADMIN" ||
          (Array.isArray(role) && role.includes("SUPER_ADMIN"))
        ) {
          axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
          setChecking(false);
          navigate("/welcome");
        } else {
          localStorage.removeItem("token");
          localStorage.removeItem("loginTime");
          localStorage.removeItem("role");
          setChecking(false);
        }
      } catch (err) {
        console.error("Token validation failed:", err);
        localStorage.removeItem("token");
        localStorage.removeItem("loginTime");
        localStorage.removeItem("role");
        setChecking(false);
      }
    };

    validateTokenAndRole();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await axios.post(
        `${BASE_URL}/api/login`,
        { email, password },
        { headers: { "Content-Type": "application/json" } },
      );

      const token = response.data?.token;
      if (!token) {
        toast.error("Login failed — token not received.", {
          position: "bottom-right",
          autoClose: 3000,
          style: { backgroundColor: "#f87171", color: "#fff" },
        });
        setLoading(false);
        return;
      }

      let decoded;
      try {
        decoded = jwtDecode(token);
      } catch (decErr) {
        console.error("JWT decode failed:", decErr);
        toast.error("Invalid token received from server", {
          position: "bottom-right",
          autoClose: 3000,
          style: { backgroundColor: "#f87171", color: "#fff" },
        });
        setLoading(false);
        return;
      }

      const roleClaim = decoded?.role ?? decoded?.roles ?? null;
      const isSuperAdmin =
        roleClaim === "SUPER_ADMIN" ||
        (Array.isArray(roleClaim) && roleClaim.includes("SUPER_ADMIN"));
      if (!isSuperAdmin) {
        toast.error("Email and password wrong", {
          position: "bottom-right",
          autoClose: 3000,
          style: { backgroundColor: "#f87171", color: "#fff" },
        });
        setLoading(false);
        return;
      }

      localStorage.setItem("token", token);
      localStorage.setItem("loginTime", Date.now().toString());
      if (decoded.role) localStorage.setItem("role", decoded.role);
      if (decoded.society) localStorage.setItem("society", decoded.society);
      if (decoded.societyRegNo)
        localStorage.setItem("societyRegNo", decoded.societyRegNo);

      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      toast.success("Login successful! Redirecting...", {
        position: "bottom-right",
        autoClose: 2000,
        style: { backgroundColor: "#10b981", color: "#fff" },
      });

      setTimeout(() => {
        setLoading(false);
        navigate("/welcome");
      }, 1500);
    } catch (err) {
      console.error("Login error:", err);
      setLoading(false);
      toast.error("Invalid credentials or server error", {
        position: "bottom-right",
        autoClose: 3000,
        style: { backgroundColor: "#f87171", color: "#fff" },
      });
    }
  };

  if (checking) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gradient-to-br from-blue-900 via-gray-900 to-black z-50">
        <div className="flex flex-col items-center space-y-6">
          <div className="relative">
            <div className="w-24 h-24 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
            <HiOutlineShieldCheck className="absolute inset-0 m-auto text-blue-400 text-4xl" />
          </div>
          <div className="text-white text-lg font-medium">
            Checking authentication...
          </div>
          <div className="text-blue-300 text-sm">Verifying your session</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 p-4 md:p-8">
      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm z-50">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-16 h-16 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
            <div className="text-white font-medium">Authenticating...</div>
            <div className="text-white/60 text-sm">
              Please wait while we verify your credentials
            </div>
          </div>
        </div>
      )}

      <div className="w-full max-w-6xl flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-12">
        {/* Left Side - Login Form */}
        <div className="w-full lg:w-1/2 max-w-md">
          <div className="rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 shadow-2xl p-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl">
                  <HiOutlineShieldCheck className="text-3xl text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">
                    Admin Portal
                  </h1>
                  <p className="text-blue-200 text-sm mt-1">
                    Super Admin Access
                  </p>
                </div>
              </div>
              <p className="text-gray-300 text-sm">
                Enter your credentials to access the management dashboard
              </p>
            </div>

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email Input */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
                  <FiMail className="text-blue-400" />
                  Super Admin Email
                </label>
                <div className="relative group">
                  <input
                    type="email"
                    className="w-full h-12 pl-12 pr-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 group-hover:border-blue-400/30"
                    placeholder="admin@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <FiMail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                </div>
              </div>

              {/* Password Input */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300">
                  <FiLock className="text-blue-400" />
                  Password
                </label>
                <div className="relative group">
                  <input
                    type={showPassword ? "text" : "password"}
                    className="w-full h-12 pl-12 pr-12 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 group-hover:border-blue-400/30"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <FiLock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <button
                    type="button"
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>

              {/* Forgot Password */}
              <div className="flex justify-end">
                <a
                  href="#"
                  className="text-sm text-blue-400 hover:text-blue-300 transition-colors hover:underline"
                >
                  Forgot password?
                </a>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/25 disabled:opacity-50 disabled:cursor-not-allowed group"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Signing In...</span>
                  </>
                ) : (
                  <>
                    <FiLogIn className="text-lg" />
                    <span>Sign In</span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Right Side - Branding/Illustration */}
        <div className="w-full lg:w-1/2 flex flex-col items-center justify-center text-center lg:text-left">
          <div className="relative max-w-lg">
            {/* Floating elements for visual interest */}
            <div className="absolute -top-6 -right-6 w-32 h-32 bg-blue-500/10 rounded-full blur-2xl"></div>
            <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-purple-500/10 rounded-full blur-2xl"></div>

            <img
              src={logo}
              alt="Admin Dashboard"
              className="relative z-10 w-full max-w-md mx-auto drop-shadow-2xl"
            />
          </div>

          <div className="mt-8 space-y-4">
            <h2 className="text-3xl font-bold text-white">
              Society Management{" "}
              <span className="text-blue-400">Dashboard</span>
            </h2>

            {/* Feature List */}
            <div className="flex flex-wrap gap-4 justify-center lg:justify-start mt-6">
              <div className="flex items-center gap-2 text-sm text-gray-300">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                Secure Authentication
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-300">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                Real-time Updates
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-300">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                Multi-role Management
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Login />} />
        <Route
          path="/welcome"
          element={
            <PrivateRoute>
              <Welcome />
            </PrivateRoute>
          }
        />
        <Route
          path="/CreateSociety"
          element={
            <PrivateRoute>
              <CreateSociety />
            </PrivateRoute>
          }
        />
        <Route
          path="/SocietyList"
          element={
            <PrivateRoute>
              <SocietyList />
            </PrivateRoute>
          }
        />
        <Route
          path="/CreateSocietyAdmin"
          element={
            <PrivateRoute>
              <CreateSocietyAdmin />
            </PrivateRoute>
          }
        />
        <Route
          path="/CreateSocietyAdminList"
          element={
            <PrivateRoute>
              <CreateSocietyAdminList />
            </PrivateRoute>
          }
        />
        <Route
          path="/AddSocietyAdmin/:email"
          element={
            <PrivateRoute>
              <AddSocietyAdmin />
            </PrivateRoute>
          }
        />
        <Route
          path="/all-admin-login"
          element={
            <PrivateRoute>
              <AllLoginAdmin />
            </PrivateRoute>
          }
        />
         <Route
          path="/request-call"
          element={
            <PrivateRoute>
              <RequestCall />
            </PrivateRoute>
          }
        />
        <Route
          path="/user-query"
          element={
            <PrivateRoute>
              <UserQuery />
            </PrivateRoute>
          }
        />
          <Route
          path="/comments"
          element={
            <PrivateRoute>
              <Comments />
            </PrivateRoute>
          }
        />
         <Route
          path="/request-demo"
          element={
            <PrivateRoute>
              <RequestDemo />
            </PrivateRoute>
          }
        />
      </Routes>
         
      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
    </Router>
  );
}

export default App;
